'use client';
import * as React from 'react';
import FolderSidebar from '@hub/common-components/src/FolderSidebar';
import ScrolledEntriesDisplay from '@hub/common-components/src/ScrolledEntriesDisplay';
import { useQuery } from '@tanstack/react-query';
import { queryFunctions } from '@hub/common/src/query-functions';
import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import LoadingSkeleton from '@hub/common-components/src/LoadingSkeleton';
import HtmlRenderer from '@hub/common-components/src/HtmlRenderer';
import ChangeBodyBackgroundColor from '@hub/common-components/src/ChangeBodyBackgroundColor';
import { useCitThemeClient } from './CitThemeProvider';

interface FolderEntriesClientProps {
  accountId: string;
  searchQuery: string | null;
  folderId?: string;
}

function SidebarToggleButton({ onClick, isOpen }: { onClick: () => void; isOpen: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`fixed left-3 z-50 w-[30px] h-[30px] rounded-full bg-white shadow-md sidebar-toggle ${
        isOpen ? 'open' : ''
      } md:hidden`}
      // read from the variable the measured height is published to rather than from context, so a
      // header animating its height moves this button without re-rendering React on every frame.
      // The fallback matches DEFAULT_HEADER_HEIGHT
      style={{ top: 'calc(var(--header-height, 70px) + 14px)' }}
      aria-label='Toggle sidebar'
    >
      {isOpen ? (
        <svg
          width='10'
          height='10'
          viewBox='0 0 12 12'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M1 1L11 11M1 11L11 1'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
          />
        </svg>
      ) : (
        <svg
          width='12'
          height='10'
          viewBox='0 0 14 12'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M1 1H13M1 6H13M1 11H13'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
          />
        </svg>
      )}
    </button>
  );
}

export default function FolderEntriesClient({
  accountId,
  searchQuery,
  folderId
}: FolderEntriesClientProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const citOrigin = useCitOriginClient();

  const { accountTheme } = useCitThemeClient();

  const { data: accountDetails, isLoading: isAccountDetailsLoading } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  const { data: folderDetails, isLoading: isFolderDetailsLoading } = useQuery({
    queryKey: ['folder-details', { accountId, folderId, citOrigin }],
    queryFn: queryFunctions.getFolderDetails
  });

  if (isAccountDetailsLoading || isFolderDetailsLoading || !accountDetails || !folderDetails) {
    // skeleton while loading
    return <LoadingSkeleton />;
  }

  const bgColor = accountTheme.backgroundColor(undefined, true);

  const bodyFontColor = accountTheme.bodyFontColor();
  const titleFontColor = accountTheme.titleFontColor();

  const bodyFont = accountTheme.bodyFont();
  const titleFont = accountTheme.titleFont();

  const lineHeight = accountTheme.lineHeight();
  const letterSpacing = accountTheme.letterSpacing();

  // Close sidebar when clicking overlay
  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className='w-full'>
      <ChangeBodyBackgroundColor color={bgColor} />

      <SidebarToggleButton
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        isOpen={isSidebarOpen}
      />

      {/* Overlay */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'show' : ''}`}
        onClick={handleOverlayClick}
      />

      <div className={`sidebar-container ${isSidebarOpen ? 'open' : ''}`}>
        <FolderSidebar
          accountId={accountDetails.slug || accountId}
          folders={accountDetails.folders || []}
          selectedFolderId={folderId}
        />
      </div>

      <div className='main-content' style={{ backgroundColor: bgColor, color: bodyFontColor }}>
        <div className='entries-wrapper'>
          <h1
            className='text-xl md:text-2xl font-bold mb-5 md:mb-3 line-clamp-1 break-all pl-10 lg:pl-0'
            style={{
              fontFamily: titleFont,
              color: titleFontColor,
              lineHeight,
              letterSpacing
            }}
          >
            {folderDetails.id
              ? `${folderDetails.name} - ${accountTheme.accountName()}`
              : `All Entries - ${accountTheme.accountName()}`}
          </h1>
          {folderDetails.description ? (
            <HtmlRenderer
              className={`text-sm mb-3 prose prose-lg prose [&_a]:underline prose-a:text-inherit marker:text-inherit max-w-none`}
              style={{
                color: `${bodyFontColor}99`,
                fontFamily: bodyFont,
                lineHeight,
                letterSpacing
              }}
              html={folderDetails.description}
            />
          ) : null}
          <ScrolledEntriesDisplay
            accountId={accountId}
            folderId={folderId}
            searchQuery={searchQuery}
          />
        </div>
      </div>
    </div>
  );
}
